import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { socket } from '../services/socket';
import { messagesService } from '../services/messages';
import type { Message, Conversation, BackendConversation } from '../services/messages';
import type { User } from '../store/slices/authSlice';
import { Socket } from 'socket.io-client';

// Define socket event types
interface ServerToClientEvents {
  new_message: (data: { 
    conversationId: string; 
    message: {
      id: string;
      senderId: string;
      content: string;
      timestamp: string;
      readAt?: string;
    }
  }) => void;
}

interface ClientToServerEvents {
  message: (data: { 
    conversation: string; 
    content: string;
  }, callback: (response: { success: boolean; error?: string }) => void) => void;
}

type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

interface MessagesContextType {
  conversations: Conversation[];
  messages: Record<string, Message[]>;
  unreadCounts: Record<string, number>;
  loading: boolean;
  error: string | null;
  sendMessage: (conversationId: string, content: string) => Promise<void>;
  loadMessages: (conversationId: string) => Promise<void>;
  loadConversations: () => Promise<void>;
}

const MessagesContext = createContext<MessagesContextType | undefined>(undefined);

export const MessagesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useSelector((state: RootState) => state.auth.user) as User | null;
  const token = useSelector((state: RootState) => state.auth.token);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Record<string, Message[]>>({});
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [pendingMessages] = useState<Set<string>>(new Set()); // Track pending message IDs
  const [sentMessages] = useState<Map<string, string>>(new Map()); // Map of tempId -> realId for sent messages

  // Add debug logging for user state
  useEffect(() => {
    console.log('MessagesContext - User state changed:', {
      hasUser: !!user,
      userId: user?._id,
      role: user?.role,
      hasToken: !!token,
      tokenLength: token?.length,
      socketConnected: socket.connected
    });
  }, [user, token]);

  const loadConversations = useCallback(async () => {
    if (!user || !user._id) {
      console.error('MessagesContext - Cannot load conversations: invalid user', {
        hasUser: !!user,
        userId: user?._id,
        role: user?.role
      });
      setError('Invalid user state');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('MessagesContext - Loading conversations for user:', {
        userId: user._id,
        role: user.role
      });
      const response = await messagesService.getConversations();
      
      // Log the raw response to see its structure
      console.log('MessagesContext - Raw response:', response);

      if (!response?.data || !Array.isArray(response.data)) {
        console.error('MessagesContext - Invalid response format:', response);
        throw new Error('Invalid response format from server');
      }

      // Transform the backend response to match our frontend format
      const transformedConversations: Conversation[] = response.data.map((conv: BackendConversation) => ({
        _id: conv.id,
        advertiser: user.role === 'advertiser' ? user._id : conv.participant.id,
        influencer: user.role === 'influencer' ? user._id : conv.participant.id,
        campaign: conv.campaignId,
        participant: {
          _id: conv.participant.id,
          username: conv.participant.name,
          profileImage: conv.participant.avatar,
          role: conv.participant.type
        },
        lastMessage: conv.lastMessage,
        createdAt: conv.timestamp,
        updatedAt: conv.timestamp
      }));

      console.log('MessagesContext - Conversations loaded:', {
        count: transformedConversations.length,
        conversations: transformedConversations.map(c => ({
          id: c._id,
          participant: {
            id: c.participant._id,
            name: c.participant.username,
            role: c.participant.role
          }
        }))
      });

      setConversations(transformedConversations);
      // Since unreadCounts is part of each conversation in the backend response
      const unreadCountsMap = response.data.reduce((acc, conv) => ({
        ...acc,
        [conv.id]: conv.unreadCount
      }), {} as Record<string, number>);
      setUnreadCounts(unreadCountsMap);
    } catch (err: any) {
      console.error('MessagesContext - Failed to load conversations:', err);
      setError(err.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const loadMessages = useCallback(async (conversationId: string) => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);
      const conversationMessages = await messagesService.getMessages(conversationId);
      setMessages(prev => ({
        ...prev,
        [conversationId]: conversationMessages
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const handleNewMessage = ({ conversationId, message }: { 
    conversationId: string; 
    message: {
      id: string;
      senderId: string;
      content: string;
      timestamp: string;
      readAt?: string;
    }
  }) => {
    if (!user) {
      console.error('MessagesContext - Cannot handle message: no user');
      return;
    }

    console.log('MessagesContext - New message received:', {
      conversationId,
      messageId: message.id,
      senderId: message.senderId,
      isOwnMessage: message.senderId === user._id
    });

    // Ignore new_message events for our own messages
    // We'll handle these through the socket acknowledgment
    if (message.senderId === user._id) {
      console.log('MessagesContext - Ignoring new_message event for own message');
      return;
    }

    // Create the new message object
    const newMessage: Message = {
      _id: message.id,
      conversation: conversationId,
      sender: message.senderId,
      content: message.content,
      createdAt: message.timestamp,
      updatedAt: message.timestamp,
      readAt: message.readAt
    };

    // Add message to state
    setMessages(prev => ({
      ...prev,
      [conversationId]: [...(prev[conversationId] || []), newMessage]
    }));
    
    // Update conversation list
    setConversations(prev => prev.map(conv => 
      conv._id === conversationId 
        ? { ...conv, lastMessage: message.content, updatedAt: message.timestamp }
        : conv
    ));

    // Update unread count
    setUnreadCounts(prev => ({
      ...prev,
      [conversationId]: (prev[conversationId] || 0) + 1
    }));
  };

  const sendMessage = async (conversationId: string, content: string) => {
    if (!user || !token || !socket.connected) {
      console.error('MessagesContext - Cannot send message:', {
        hasUser: !!user,
        hasToken: !!token,
        socketConnected: socket.connected
      });
      throw new Error('Not connected to chat server');
    }

    try {
      console.log('MessagesContext - Sending message:', { 
        conversationId, 
        content,
        userId: user._id,
        socketId: socket.id
      });
      
      // Create optimistic message with a unique ID
      const tempId = 'temp-' + Date.now();
      const optimisticMessage: Message = {
        _id: tempId,
        conversation: conversationId,
        sender: user._id,
        content,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Add optimistic message to state
      setMessages(prev => ({
        ...prev,
        [conversationId]: [
          ...(prev[conversationId] || []),
          optimisticMessage
        ]
      }));

      // Update conversation last message
      setConversations(prev => 
        prev.map(conv => 
          conv._id === conversationId
            ? { 
                ...conv, 
                lastMessage: content, 
                updatedAt: new Date().toISOString() 
              }
            : conv
        )
      );

      // Send message through socket with acknowledgment
      await new Promise<void>((resolve, reject) => {
        (socket as AppSocket).emit('message', { 
          conversation: conversationId, 
          content 
        }, (response: { success: boolean; error?: string; messageId?: string }) => {
          if (response.success) {
            console.log('Message sent successfully:', response);
            // Update the optimistic message with the real ID
            if (response.messageId) {
              setMessages(prev => ({
                ...prev,
                [conversationId]: (prev[conversationId] || []).map(msg =>
                  msg._id === tempId ? { ...msg, _id: response.messageId! } : msg
                )
              }));
            }
            resolve();
          } else {
            console.error('Failed to send message:', response.error);
            // Remove optimistic message on error
            setMessages(prev => ({
              ...prev,
              [conversationId]: (prev[conversationId] || []).filter(
                msg => msg._id !== tempId
              )
            }));
            reject(new Error(response.error || 'Failed to send message'));
          }
        });

        // Set timeout for acknowledgment
        setTimeout(() => {
          // Remove optimistic message on timeout
          setMessages(prev => ({
            ...prev,
            [conversationId]: (prev[conversationId] || []).filter(
              msg => msg._id !== tempId
            )
          }));
          reject(new Error('Message send timeout'));
        }, 5000);
      });
    } catch (error) {
      console.error('MessagesContext - Failed to send message:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (!user || !user._id || !token) {
      console.log('MessagesContext - No valid user or token, disconnecting socket:', {
        hasUser: !!user,
        userId: user?._id,
        hasToken: !!token
      });
      socket.disconnect();
      setIsSocketConnected(false);
      return;
    }

    console.log('MessagesContext - Setting up socket connection:', {
      userId: user._id,
      role: user.role,
      hasToken: !!token,
      socketConnected: socket.connected
    });

    // Connect to WebSocket
    socket.auth = { token, userId: user._id };
    if (!socket.connected) {
      socket.connect();
    }

    const handleConnect = () => {
      console.log('MessagesContext - Socket connected:', {
        socketId: socket.id,
        userId: user._id
      });
      setIsSocketConnected(true);
      loadConversations();
    };

    const handleDisconnect = (reason: string) => {
      console.log('MessagesContext - Socket disconnected:', {
        reason,
        userId: user._id
      });
      setIsSocketConnected(false);
    };

    const handleConnectError = (error: Error) => {
      console.error('MessagesContext - Socket connection error:', {
        error: error.message,
        userId: user._id
      });
      setIsSocketConnected(false);
    };

    // Set up socket event listeners
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);
    (socket as AppSocket).on('new_message', handleNewMessage);

    // Load initial conversations
    if (socket.connected) {
      loadConversations();
    }

    return () => {
      console.log('MessagesContext - Cleaning up socket connection');
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      (socket as AppSocket).off('new_message', handleNewMessage);
    };
  }, [user, token, loadConversations]);

  return (
    <MessagesContext.Provider
      value={{
        conversations,
        messages,
        unreadCounts,
        loading,
        error,
        sendMessage,
        loadMessages,
        loadConversations,
      }}
    >
      {children}
    </MessagesContext.Provider>
  );
};

export const useMessages = () => {
  const context = useContext(MessagesContext);
  if (context === undefined) {
    throw new Error('useMessages must be used within a MessagesProvider');
  }
  return context;
}; 